import { Upload } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { useRef, useState, type ReactNode } from 'react';
import { MAX_UPLOAD_MB, UPLOAD_ACCEPT, uploadProblem } from '@/lib/portal';
import { cn } from '@/lib/utils';

/**
 * Drag-and-drop zone with a Browse link (document 05: attachments drop zone, 20 MB per file).
 * Files the browser can already tell are wrong are reported, not sent.
 */
export function FileDrop({
  onFiles,
  onProblem,
  disabled,
  compact,
  label,
  accept = UPLOAD_ACCEPT,
  types,
  children,
}: {
  onFiles: (files: File[]) => void;
  onProblem: (message: string) => void;
  disabled?: boolean;
  compact?: boolean;
  label?: string;
  /** The file picker's filter (default: PDFs and photos). */
  accept?: string;
  /** What may be dropped, shown under the label. */
  types?: string;
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const t = useT();
  const [over, setOver] = useState(false);
  const take = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    const ok: File[] = [];
    for (const f of files) {
      const p = uploadProblem(f);
      if (p) onProblem(p);
      else ok.push(f);
    }
    if (ok.length) onFiles(ok);
  };
  return (
    <div
      data-testid="file-drop"
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) take(e.dataTransfer.files);
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-card text-center text-sm text-muted-foreground',
        compact ? 'px-3 py-3' : 'px-6 py-8',
        over && 'border-primary bg-primary/5',
        disabled && 'opacity-60',
      )}
    >
      {!compact ? <Upload className="mb-1 size-5" aria-hidden /> : null}
      <span>
        {label ?? t('files.dropHere')}{' '}
        <button
          type="button"
          className="font-medium text-primary hover:underline disabled:no-underline"
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          {t('files.browse')}
        </button>
      </span>
      <span className="text-xs">
        {t('files.upTo', { types: types ?? t('files.types'), mb: MAX_UPLOAD_MB })}
      </span>
      {children}
      <input
        ref={input}
        type="file"
        multiple
        accept={accept}
        className="sr-only"
        aria-label={t('files.chooseFiles')}
        onChange={(e) => {
          take(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
